import { action } from "typesafe-actions";
import {
  IFiscalPeriod,
  IFiscalPeriodList,
  IReopenPreview,
  IRecloseRangeResponse,
} from "./FiscalPeriods.types";
import { IDataTableV2DateState } from "../../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { ERROR_MESSAGES } from "../../../constants/enums";
import { translateBackendError } from "../../../helpers/errorTranslator";

export const FETCH_FISCAL_PERIODS_LIST_PROGRESS =
  "FETCH_FISCAL_PERIODS_LIST_PROGRESS";
export const FETCH_FISCAL_PERIODS_LIST_SUCCESS =
  "FETCH_FISCAL_PERIODS_LIST_SUCCESS";
export const FETCH_FISCAL_PERIODS_LIST_FAILED =
  "FETCH_FISCAL_PERIODS_LIST_FAILED";

export const fetchFiscalPeriodsListProgress = () =>
  action(FETCH_FISCAL_PERIODS_LIST_PROGRESS);
export const fetchFiscalPeriodsListSuccess = (
  data: IFiscalPeriodList[],
  totalRecords: number,
) => action(FETCH_FISCAL_PERIODS_LIST_SUCCESS, { data, totalRecords });
export const fetchFiscalPeriodsListFailed = () =>
  action(FETCH_FISCAL_PERIODS_LIST_FAILED);

export const fetchFiscalPeriodsListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchFiscalPeriodsListProgress());
      let finalUrl = `/user/get-fiscal-year?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue && searchValue.length > 0) {
        finalUrl = `/user/get-fiscal-year?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&advanceFilter=${JSON.stringify(
          searchValue,
        )}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      const res = await api.get(finalUrl);
      const data: IFiscalPeriodList[] = res.data.data;
      dispatch(fetchFiscalPeriodsListSuccess(data, res.data.totalRecords));
    } catch (err: any) {
      dispatch(fetchFiscalPeriodsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_FISCAL_PERIODS_PROGRESS = "FETCH_FISCAL_PERIODS_PROGRESS";
export const FETCH_FISCAL_PERIODS_SUCCESS = "FETCH_FISCAL_PERIODS_SUCCESS";
export const FETCH_FISCAL_PERIODS_FAILED = "FETCH_FISCAL_PERIODS_FAILED";

export const fetchFiscalPeriodsProgress = () =>
  action(FETCH_FISCAL_PERIODS_PROGRESS);
export const fetchFiscalPeriodsSuccess = (data: IFiscalPeriod[]) =>
  action(FETCH_FISCAL_PERIODS_SUCCESS, { data });
export const fetchFiscalPeriodsFailed = (error: string) =>
  action(FETCH_FISCAL_PERIODS_FAILED, { error });

export const fetchFiscalPeriodsAsync =
  (year: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchFiscalPeriodsProgress());

      const res = await api.get(
        `/user/get-fiscal-year-month?fiscal_years_code=${year}`,
      );
      const data: IFiscalPeriod[] = res.data.data;

      dispatch(fetchFiscalPeriodsSuccess(data));
    } catch (err: any) {
      dispatch(fetchFiscalPeriodsFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertFiscalPeriodsAsync =
  (
    data: IFiscalPeriod,
    onCallback: (isSuccess: boolean, data?: IFiscalPeriod) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/user/edit-fiscal-year-month", {
        ...data,
      });
      const message = "Fiscal period saved successfully.";
      dispatch(
        showMessage({
          type: "success",
          message: message,
          displayAs: "snackbar",
        }),
      );
      onCallback(true, res.data.data[0]);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const generateFiscalPeriodsReportAsync =
  (
    data: {
      fiscal_years_code: string | null;
      fiscal_year_month_code:string | null;
      period: string | null;
      year: string | null;
      from: string | null;
      to: string | null;
      report_code: string | null;
    },
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/accounting/generate-fiscal-closing-reports", {
        ...data,
      });
      const message = "Report created successfully.";
      dispatch(
        showMessage({
          type: "success",
          message: message,
          displayAs: "snackbar",
        }),
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const createFiscalPeriodAsync =
  (
    data: {
      year: string;
      status: string;
    },
    onCallback: (isSuccess: boolean, data?: IFiscalPeriod) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/user/edit-fiscal-year", {
        period: null,
        year: data.year,
        status: data.status,
      });
      const message = "Fiscal period created successfully.";
      dispatch(
        showMessage({
          type: "success",
          message: message,
          displayAs: "snackbar",
        }),
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchFiscalPeriodsYearsAsync =
  (
    onCallback: (data: string[]) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(`user/get-fiscal-year-list`);
      const data: string[] = res.data.data;
      onCallback(data);
    } catch (err: any) {
      onCallback([]);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_REOPEN_PREVIEW_PROGRESS = "FETCH_REOPEN_PREVIEW_PROGRESS";
export const REOPEN_PREVIEW_SUCCESS = "REOPEN_PREVIEW_SUCCESS";
export const REOPEN_PREVIEW_FAILED = "REOPEN_PREVIEW_FAILED";

export const fetchReopenPreviewProgress = () =>
  action(FETCH_REOPEN_PREVIEW_PROGRESS);
export const reopenPreviewSuccess = (data: IReopenPreview) =>
  action(REOPEN_PREVIEW_SUCCESS, { data });
export const reopenPreviewFailed = () => action(REOPEN_PREVIEW_FAILED);

export const fetchReopenPreviewAsync =
  (
    fiscal_year_month_code: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchReopenPreviewProgress());

      const res = await api.get(
        `/accounting/fiscal-year-month/reopen-preview?fiscal_year_month_code=${fiscal_year_month_code}`,
      );
      const raw = res.data?.data ?? {};
      // Normalize the backend payload into the IReopenPreview shape. The
      // backend may use alternate key names (e.g. snake_case), so coerce the
      // fields defensively and guarantee the arrays/counts are always present
      // to avoid runtime crashes in ReopenPreviewDialog.
      const data: IReopenPreview = {
        months: Array.isArray(raw.months)
          ? raw.months
          : Array.isArray(raw.affected_months)
            ? raw.affected_months
            : [],
        summaries: raw.summaries ?? raw.summary_count ?? 0,
        yearCloses: raw.yearCloses ?? raw.year_closes ?? 0,
        fiscal_years_reopened: Array.isArray(raw.fiscal_years_reopened)
          ? raw.fiscal_years_reopened
          : [],
      };
      dispatch(reopenPreviewSuccess(data));
    } catch (err: any) {
      // If the reopen-preview endpoint is not available yet (404), fall back to
      // a client-side calendar walk that computes the affected months from the
      // fiscal periods already loaded in Redux state. The snapshot and
      // year-close counts are unknown client-side, so they are reported as 0.
      if (err.response?.status === 404) {
        const periods = getState().accounting.fiscalPeriods.data;
        const startRow = periods.find(
          (period) => period.fiscal_year_month_code === fiscal_year_month_code,
        );

        if (startRow && startRow.from) {
          const affected = periods
            .filter(
              (period) => period.from !== null && period.from >= startRow.from!,
            )
            .sort((a, b) => (a.from! < b.from! ? -1 : a.from! > b.from! ? 1 : 0));

          const fallback: IReopenPreview = {
            months: affected
              .map((period) => period.fiscal_year_month_code)
              .filter((code): code is string => code !== null),
            summaries: 0,
            yearCloses: 0,
            fiscal_years_reopened: Array.from(
              new Set(
                affected
                  .map((period) => period.fiscal_years_code)
                  .filter((code): code is string => code !== null),
              ),
            ),
          };
          dispatch(reopenPreviewSuccess(fallback));
          return;
        }
      }

      dispatch(reopenPreviewFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

export const RECLOSE_RANGE_PROGRESS = "RECLOSE_RANGE_PROGRESS";
export const RECLOSE_RANGE_SUCCESS = "RECLOSE_RANGE_SUCCESS";
export const RECLOSE_RANGE_FAILED = "RECLOSE_RANGE_FAILED";

export const recloseRangeProgress = () => action(RECLOSE_RANGE_PROGRESS);
export const recloseRangeSuccess = (data: IRecloseRangeResponse) =>
  action(RECLOSE_RANGE_SUCCESS, { data });
export const recloseRangeFailed = () => action(RECLOSE_RANGE_FAILED);

export const recloseRangeAsync =
  (
    fiscal_year_month_code: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(recloseRangeProgress());

      const res = await api.post("/user/fiscal-year-month/reclose-range", {
        fiscal_year_month_code,
      });
      const data: IRecloseRangeResponse = res.data.data;
      dispatch(recloseRangeSuccess(data));
    } catch (err: any) {
      dispatch(recloseRangeFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

export const fetchFiscalClosingReportLogAsync =
  (
    fiscal_year_month_code: string,
    onCallback: (isSuccess: boolean, errorLog?: string | null) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/accounting/get-fiscal-closing-reports?fiscal_year_month_code=${fiscal_year_month_code}&status=FAILED&pageNo=1&itemPerPage=1`,
      );
      const logs = res.data?.data ?? [];
      const errorLog: string | null = logs.length > 0 ? logs[0].error_log : null;
      onCallback(true, errorLog);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

export type TGlEntryFlowType =
  | "INVOICE"
  | "BILL"
  | "INVOICE_PAYMENT"
  | "BILL_PAYMENT"
  | "JOURNAL_ENTRY";

export interface IProcessGlEntriesPayload {
  proccess_ids: string[];
  flow_type: TGlEntryFlowType;
  from_date: string | null;
  to_date: string | null;
  page_size: number;
  delay_ms: number;
  current_page: number;
  completed_records: number;
  process_code: string | null;
  is_close: boolean;
}

export const processGlEntriesAsync =
  (
    payload: IProcessGlEntriesPayload,
    onCallback: (isSuccess: boolean, data?: any) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/accounting/process-gl-entries", {
        ...payload,
      });
      dispatch(
        showMessage({
          type: "success",
          message: `GL entries processed for ${payload.flow_type}.`,
          displayAs: "snackbar",
        }),
      );
      onCallback(true, res.data?.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export interface IGlReconciliationError {
  source_item_id: number | null;
  error: string | null;
  log_status: string | null;
  create_ts: string | null;
  gl_flow_logs_code: string | null;
}

export interface IGlReconciliationProcessRun {
  process_code: string | null;
  status: string | null;
  from_date: string | null;
  to_date: string | null;
  completed_records: number | null;
  failed_records: number | null;
  current_page: number | null;
  create_ts: string | null;
}

export interface IGlReconciliationSummary {
  flow_type: string;
  source_table: string | null;
  temp_entry_type: string | null;
  expected_count: number;
  inserted_count: number;
  failed_count: number;
  missing_count: number;
  latest_process_code: string | null;
  latest_process_status: string | null;
  process_runs: IGlReconciliationProcessRun[];
  errors: IGlReconciliationError[];
}

export interface IGlReconciliationResponse {
  from_date: string | null;
  to_date: string | null;
  summary: IGlReconciliationSummary[];
  totals: {
    expected: number;
    inserted: number;
    failed: number;
    missing: number;
  };
}

export const fetchGlEntryReconciliationAsync =
  (
    params: {
      from_date: string | null;
      to_date: string | null;
      flow_type?: string;
    },
    onCallback: (
      isSuccess: boolean,
      data?: IGlReconciliationResponse,
    ) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const query = new URLSearchParams();
      if (params.from_date) {
        query.append("from_date", params.from_date);
      }
      if (params.to_date) {
        query.append("to_date", params.to_date);
      }
      if (params.flow_type) {
        query.append("flow_type", params.flow_type);
      }
      const res = await api.get(
        `/accounting/gl-entry-reconciliation?${query.toString()}`,
      );
      onCallback(true, res.data?.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const reRunProcessGlEntriesAsync =
  (
    process_code: string | null,
    onCallback: (isSuccess: boolean, data?: any) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/accounting/re-run-process-gl-entries", {
        process_code,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "GL entries re-run started.",
          displayAs: "snackbar",
        }),
      );
      onCallback(true, res.data?.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export type TAiReconcileDepth = "quick" | "deep";

export type TAiReconcileReportType =
  | "TRIAL_BALANCE"
  | "BALANCE_SHEET"
  | "OPERATING_STATEMENT"
  | "AR_AUDIT";

export interface IAiReconcilePayload {
  report_type: TAiReconcileReportType;
  from_date: string | null;
  to_date: string | null;
  gl_account_codes: string[];
  depth: TAiReconcileDepth;
}

export const aiReconcileAsync =
  (
    payload: IAiReconcilePayload,
    onCallback: (isSuccess: boolean, data?: any) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/accounting/ai-reconcile", {
        ...payload,
      });
      dispatch(
        showMessage({
          type: "success",
          message:
            payload.depth === "deep"
              ? `AI reconcile (deep) started for ${payload.report_type}.`
              : `AI reconcile completed for ${payload.report_type}.`,
          displayAs: "snackbar",
        }),
      );
      onCallback(true, res.data?.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export interface IAiReconcileFinding {
  metric?: { name?: string; value?: string } | null;
  category?: string | null;
  severity?: string | null;
  description?: string | null;
  suggested_action?: string | null;
  affected_account_codes?: string[] | null;
}

export interface IAiReconcileExceptionLine {
  debit: number | null;
  credit: number | null;
  gl_account_code: string | null;
  gl_account_name: string | null;
}

export interface IAiReconcileException {
  net: number | null;
  date: string | null;
  type: string | null;
  lines?: IAiReconcileExceptionLine[] | null;
  debits: number | null;
  credits: number | null;
  customer: string | null;
  source_id: number | string | null;
  policy_number: string | null;
}

export interface IAiReconcileControl {
  type: string | null;
  count: number | null;
  detail: string | null;
}

export interface IAiReconcileTotalsByType {
  type: string | null;
  debits: number | null;
  credits: number | null;
  difference: number | null;
  line_count: number | null;
  transaction_count: number | null;
}

export interface IAiReconcileRelatedTransaction {
  date: string | null;
  type: string | null;
  debits: number | null;
  credits: number | null;
  customer: string | null;
  source_id: number | string | null;
  policy_number: string | null;
  gl_account_code: string | null;
  gl_account_name: string | null;
}

export interface IAiReconcileAggregates {
  counts?: {
    types?: number | null;
    controls?: number | null;
    exceptions?: number | null;
    exceptions_limit_hit?: boolean | null;
    related_transactions?: number | null;
  } | null;
  period?: { from_date?: string | null; to_date?: string | null } | null;
  overall?: {
    debits?: number | null;
    credits?: number | null;
    difference?: number | null;
  } | null;
  controls?: IAiReconcileControl[] | null;
  exceptions?: IAiReconcileException[] | null;
  totals_by_type?: IAiReconcileTotalsByType[] | null;
  related_transactions?: IAiReconcileRelatedTransaction[] | null;
}

export interface IAiReconcileResult {
  reconcile_code: string | null;
  report_type: TAiReconcileReportType | string | null;
  depth: TAiReconcileDepth | string | null;
  from_date: string | null;
  to_date: string | null;
  gl_account_codes?: string[] | null;
  model?: string | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
  cache_read_input_tokens?: number | null;
  cache_creation_input_tokens?: number | null;
  findings?: IAiReconcileFinding[] | null;
  summary: string | null;
  aggregates?: IAiReconcileAggregates | null;
  status: string | null;
  error?: string | null;
  created_by_id?: string | null;
  created_by_name?: string | null;
  modified_by_id?: string | number | null;
  modified_by_name?: string | null;
  create_ts: string | null;
  [key: string]: any;
}

export interface IFetchAiReconcileResultParams {
  reconcile_code?: string | null;
  report_type?: TAiReconcileReportType | string | null;
  from_date?: string | null;
  to_date?: string | null;
}

// Fetch stored AI reconciliation results. Called without filters it returns all
// stored runs; pass report_type (or reconcile_code / dates) to narrow it down.
// Used to poll a depth=deep run or re-read a completed depth=quick run.
export const fetchAiReconcileResultAsync =
  (
    params: IFetchAiReconcileResultParams,
    onCallback: (isSuccess: boolean, data?: IAiReconcileResult[]) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const query = new URLSearchParams();
      if (params.reconcile_code) {
        query.append("reconcile_code", params.reconcile_code);
      }
      if (params.report_type) {
        query.append("report_type", params.report_type);
      }
      if (params.from_date) {
        query.append("from_date", params.from_date);
      }
      if (params.to_date) {
        query.append("to_date", params.to_date);
      }
      const queryString = query.toString();
      const res = await api.get(
        `/accounting/gl-ai-reconcile-result${
          queryString ? `?${queryString}` : ""
        }`,
      );
      // Normalize to an array regardless of whether the backend returns a
      // single object or a list.
      const raw = res.data?.data;
      const list: IAiReconcileResult[] = Array.isArray(raw)
        ? raw
        : raw
          ? [raw]
          : [];
      onCallback(true, list);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_FISCAL_PERIODS_STATE = "CLEAR_FISCAL_PERIODS_STATE";
export const clearFiscalPeriodsState = () => action(CLEAR_FISCAL_PERIODS_STATE);
